import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useToast } from '../components/ToastContainer';
import useAdminStore from '../stores/useAdminStore';
import useAuthStore from '../stores/useAuthStore';

const AdminCategoryManagement = () => {
    const { user } = useAuthStore();
    const {
        categories,
        fetchCategories,
        createCategory,
        updateCategory,
        deleteCategory,
        isLoading,
        error,
    } = useAdminStore();
    const toast = useToast();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        parent: '',
        isActive: true,
        order: 0
    });


    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);


    console.log(categories)

    const handleOpenModal = (category = null) => {
        if (category) {
            setEditingCategory(category);
            setFormData({
                name: category?.name,
                description: category?.description || '',
                parent: category?.parent || '',
                isActive: category?.isActive,
                order: category?.order
            });
        } else {
            setEditingCategory(null);
            setFormData({
                name: '',
                description: '',
                parent: '',
                isActive: true,
                order: 0
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingCategory(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCategory) {
                await updateCategory(editingCategory._id, formData);
                toast.success('Category updated successfully');
            } else {
                await createCategory(formData);
                toast.success('Category created successfully');
            }
            handleCloseModal();
        } catch (error) {
            const message = error.response?.data?.error || 'Operation failed';
            toast.error(message);
        }
    };

    const handleDelete = async (categoryId, categoryName) => {
        if (!window.confirm(`Are you sure you want to delete "${categoryName}"? This cannot be undone.`)) return;
        try {
            await deleteCategory(categoryId);
            toast.success('Category deleted successfully');
        } catch (error) {
            const message = error.response?.data?.error || 'Failed to delete category';
            toast.error(message);
        }
    };

    const renderCategoryTree = (items, depth = 0) => {
        return items?.map(category => (
            <div key={category?._id} className="mb-2">
                <div className="flex items-center p-3 bg-white dark:bg-neutral-800 rounded border border-neutral-200 dark:border-neutral-700">
                    <div className="flex-1">
                        <div className="flex items-center">
                            {depth > 0 && (
                                <span className="mr-2 text-neutral-400">└─</span>
                            )}
                            <span className="font-medium text-neutral-900 dark:text-white">{category?.name}</span>
                            {!category?.isActive && (
                                <span className="ml-2 px-2 py-0.5 text-xs bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300 rounded">
                                    Inactive
                                </span>
                            )}
                        </div>
                        {category?.description && (
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">{category?.description}</p>
                        )}
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                            Order: {category?.order} | Slug: {category?.slug}
                        </p>
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => handleOpenModal(category)}
                            className="px-3 py-1 text-sm bg-primary-500 hover:bg-primary-600 text-white rounded"
                        >
                            Edit
                        </button>
                        <button
                            onClick={() => handleDelete(category?._id, category?.name)}
                            className="px-3 py-1 text-sm bg-danger-500 hover:bg-danger-600 text-white rounded"
                        >
                            Delete
                        </button>
                    </div>
                </div>
                {category?.children && category?.children?.length > 0 && (
                    <div className="ml-6 mt-2">
                        {renderCategoryTree(category?.children, depth + 1)}
                    </div>
                )}
            </div>
        ));
    };

    if (error) {
        return (
            <DashboardLayout userRole={user.role}>
                <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Error loading analytics data</h2>
                        <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error}</p>
                        <button
                            onClick={() => {
                                fetchCategories();
                            }}
                            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    if (isLoading) {
        return (
            <DashboardLayout userRole={user.role}>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole={user.role}>
            <div className="py-8">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-6 flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">Category Management</h1>
                            <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                                Create and manage nested course categories
                            </p>
                        </div>
                        <button
                            onClick={() => handleOpenModal()}
                            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            Add Category
                        </button>
                    </div>

                    <div className="space-y-4">
                        {categories.length > 0 ? (
                            renderCategoryTree(categories)
                        ) : (
                            <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700">
                                <div className="text-6xl mb-4">📁</div>
                                <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No categories yet</h3>
                                <p className="text-neutral-600 dark:text-neutral-400">
                                    Create your first category to organize courses.
                                </p>
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-neutral-900 opacity-75" onClick={handleCloseModal}></div>
                        </div>

                        <div className="inline-block align-bottom bg-white dark:bg-neutral-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white dark:bg-neutral-800 px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                                <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
                                    {editingCategory ? 'Edit Category' : 'Create Category'}
                                </h3>
                            </div>
                            <form onSubmit={handleSubmit} className="px-6 py-4">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            Name *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            Description
                                        </label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            Parent Category
                                        </label>
                                        <select
                                            value={formData.parent}
                                            onChange={(e) => setFormData({ ...formData, parent: e.target.value })}
                                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value="">None (Top-level)</option>
                                            {flattenCategories(categories).map(cat => (
                                                <option key={cat._id} value={cat._id}>
                                                    {cat.path}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                                Order
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.order}
                                                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                                                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                        <div className="flex items-end">
                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.isActive}
                                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Active</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        {editingCategory ? 'Update' : 'Create'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

// const flattenCategories = (categories, prefix = '', depth = 0) => {
//     let result = [];
//     categories.forEach(cat => {
//         const path = depth === 0 ? cat?.name : `${prefix} > ${cat?.name}`;
//         result.push({ ...cat, path });
//         if (cat?.children && cat?.children?.length > 0) {
//             result = result.concat(flattenCategories(cat?.children, path, depth + 1));
//         }
//     });
//     return result;
// };


const flattenCategories = (categories, prefix = '') => {
    let result = [];

    categories.forEach(cat => {
        const path = prefix ? `${prefix} > ${cat.name}` : cat.name;

        result.push({
            _id: cat._id,
            name: cat.name,
            path
        });

        if (cat.children?.length) {
            result = result.concat(flattenCategories(cat.children, path));
        }
    });

    return result;
};

export default AdminCategoryManagement;